import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FiArrowRight,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiArrowLeft,
} from "react-icons/fi";
import { useInView } from "../components/useInView";

/* ── Portfolio images – static imports (fixes blank screen) ── */
import aerosud01 from "../assets/images/portfolio/aerosud_01.jpg";
import aerosud02 from "../assets/images/portfolio/aerosud_02.jpg";
import aerosud03 from "../assets/images/portfolio/aerosud_03.jpg";
import aerosud04 from "../assets/images/portfolio/aerosud_04.jpg";
import aerosud05 from "../assets/images/portfolio/aerosud_05.jpg";
import columns01  from "../assets/images/portfolio/columns_01.jpg";
import columns02  from "../assets/images/portfolio/columns_02.jpg";
import columns03  from "../assets/images/portfolio/columns_03.jpg";
import columns04  from "../assets/images/portfolio/columns_04.jpg";
import columns05  from "../assets/images/portfolio/columns_05.jpg";
import ferrobank01 from "../assets/images/portfolio/ferrobank-de-grit-01.jpg";
import ferrobank02 from "../assets/images/portfolio/ferrobank-de-grit-02.jpg";
import ferrobank03 from "../assets/images/portfolio/ferrobank-de-grit-03.jpg";
import g4mining01  from "../assets/images/portfolio/g4-mining_01.jpg";
import g4mining02  from "../assets/images/portfolio/g4-mining_02.jpg";
import g4mining03  from "../assets/images/portfolio/g4-mining_03.jpg";
import g4mining04  from "../assets/images/portfolio/g4-mining_04.jpg";
import dannhauser02 from "../assets/images/portfolio/dannhauser_02.jpg";
import dannhauser03 from "../assets/images/portfolio/dannhauser_03.jpg";
import kuruman01   from "../assets/images/portfolio/kuruman_01.jpg";
import kuruman05   from "../assets/images/portfolio/kuruman_05.jpg";
import steelpoort05 from "../assets/images/portfolio/steelpoort_05.jpg";
import steelpoort06 from "../assets/images/portfolio/steelpoort_06.jpg";

import "./ServiceDetail.css";

/* ─────────────────────────────────────────────
   DISABLE RIGHT-CLICK (matches About.jsx)
───────────────────────────────────────────── */
function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* ─────────────────────────────────────────────
   SERVICE NAVIGATION ORDER
───────────────────────────────────────────── */
const ALL_SERVICES = [
  { slug: "framework-and-scaffolding", short: "Formwork & Scaffolding" },
  { slug: "columns",                   short: "Columns" },
  { slug: "retaining-walls",           short: "Retaining Walls" },
  { slug: "suspended-slab",            short: "Suspended Slab" },
  { slug: "ring-beam",                 short: "Ring Beam" },
  { slug: "transport",                 short: "Transport" },
  { slug: "temporary-work-design",     short: "Temporary Work Design" },
  { slug: "pr-engineer-services",      short: "PR Engineer Services" },
];

/* ─────────────────────────────────────────────
   SERVICE DATA
───────────────────────────────────────────── */
const serviceData = {

  /* ══════════════════════════════════════════
     1. FRAMEWORK & SCAFFOLDING
  ══════════════════════════════════════════ */
  "framework-and-scaffolding": {
    title: "Framework & Scaffolding",
    subtitle: "Formwork · Scaffolding · Temporary Structures",
    heroLabel: "TEMPORARY WORKS",
    accentStat: [
      { val: "28",  unit: "days",  label: "Typical Slab Cure Time" },
      { val: "4",   unit: "types", label: "Scaffold Categories" },
      { val: "6",   unit: "m max", label: "Safe Frame Height" },
    ],
    images: [
      { src: aerosud01, caption: "Engineered Formwork System" },
      { src: aerosud02, caption: "High-Rise Scaffolding" },
      { src: aerosud03, caption: "Column Formwork" },
      { src: aerosud04, caption: "Slab Propping System" },
      { src: aerosud05, caption: "Independent Scaffold Structure" },
    ],
    intro:
      "Formwork and scaffolding are the backbone of any concrete construction project. We design, supply, and erect systems tailored to your project's exact requirements — from small residential builds to large-scale industrial complexes.",
    sections: [
      {
        heading: "Formwork",
        tag: "MOULDS & SUPPORTS",
        body: "Formwork refers to temporary or permanent moulds into which concrete is poured. It holds the concrete in the desired shape until it has cured sufficiently, such as for columns, beams, or slabs.\n\nFor vertical surfaces, formwork is typically removed after about 24 hours. For beams and slabs, the props and forms are usually left in place until the concrete reaches adequate strength (often after 28 days). Alternatively, the forms can be removed earlier and replaced with back propping for horizontal elements.\n\nFormwork must be strong enough to support all loads imposed during concreting. It needs to be rigid and grout-tight during compaction and vibration — especially when a high-quality direct finish (off-shutter finish) is required. The final concrete surface will reflect the texture and shape of the formwork material and panels used. These finishes are available in different grades or classes.",
        list: null,
      },
      {
        heading: "Types of Formwork",
        tag: "SYSTEMS",
        body: null,
        list: [
          {
            label: "Traditional Timber Formwork",
            detail: "Constructed on site using timber. Relatively easy to produce but becomes time-consuming for larger structures and has a limited lifespan.",
          },
          {
            label: "Engineered / Modern Formwork Systems",
            detail: "Prefabricated modules with a steel frame and contact surface made of steel, timber, or other suitable materials. Significantly faster construction and lower life-cycle costs compared to traditional timber. Generally divided into horizontal and vertical systems.",
          },
          {
            label: "Stay-in-Place Formwork Systems",
            detail: "Assembled on site using prefabricated insulating concrete forms (ICFs) or fibre-reinforced forms. The formwork remains permanently after the concrete cures. Provides additional axial and shear reinforcement, confines the concrete, and protects against environmental damage.",
          },
        ],
      },
      {
        heading: "Scaffolding",
        tag: "ACCESS PLATFORMS",
        body: "Scaffolding is a temporary elevated platform supported by a framework erected on site. It provides safe access for trades (e.g., bricklaying, plastering, and painting) and supports construction materials.\n\nIt is usually a modular system made of metal or aluminium, although other materials can be used. Scaffold boards or planks must be strong enough to support both workers and materials. Wooden planks should be at least 228 mm wide and 50 mm thick, no longer than approximately 4.8 m, and supported at a maximum span of 2.2 m. Steel planks, typically 228 mm wide, clip directly into the framework.\n\nTo maintain a safe, consistent gap between the scaffold and the structure, and to ensure overall stability, ties are used to securely connect the scaffold to the building.",
        list: null,
      },
      {
        heading: "Categories of Scaffolding",
        tag: "TYPES",
        body: null,
        list: [
          {
            label: "Steel Builders' Trestles",
            detail: "Suitable for most interior and external work up to about 2.4 m high (single-storey buildings). Lightweight, easily handled by one person, and fold for convenient storage and transport. Common heights range from 800–1000 mm extending to ~1800 mm for ceiling work, and 1200–1400 mm extending to ~2400 mm for brickwork.",
          },
          {
            label: "Scaffold Frames (Speed Frames)",
            detail: "Quick to erect, easy to transport, and simple to store after dismantling. However, they become unsafe if heights exceed 6.0 m. Best suited for short-duration tasks such as painting or installing fixtures.",
          },
          {
            label: "Independent Scaffolding",
            detail: "Consists of at least two rows of vertical standards and is self-supporting, relying on the building only for stability ties. Must be securely tied to the building at alternate lift heights vertically and at no more than 6.0 m centres horizontally.",
          },
          {
            label: "Mobile Scaffolding",
            detail: "Wheeled towers that can be relocated easily. Ideal for indoor maintenance and finishing work where mobility is required across large floor areas.",
          },
        ],
      },
    ],
  },

  /* ══════════════════════════════════════════
     2. COLUMNS
  ══════════════════════════════════════════ */
  "columns": {
    title: "Columns",
    subtitle: "DUO Formwork System · Foundations · Walls · Slabs",
    heroLabel: "STRUCTURAL COLUMNS",
    accentStat: [
      { val: "550", unit: "mm max", label: "Column Size (DUO)" },
      { val: "80",  unit: "kN/m²", label: "Max Concrete Pressure" },
      { val: "0",   unit: "crane",  label: "Crane-Free Assembly" },
    ],
    images: [
      { src: columns01, caption: "DUO Column Formwork" },
      { src: columns02, caption: "Polymer Panel System" },
      { src: columns03, caption: "Column Assembly" },
      { src: columns04, caption: "Finished Column Structure" },
      { src: columns05, caption: "Multi-Column Project" },
    ],
    intro:
      "Columns are vertical structural members primarily designed to support compressive loads. Using the innovative DUO formwork system, we deliver fast, crane-free solutions for any column or slab configuration — ensuring both speed and precision on site.",
    sections: [
      {
        heading: "DUO Formwork System",
        tag: "INNOVATION",
        body: "DUO is a versatile modern formwork system that can be used for foundations, walls, columns, slabs, and beams.\n\nThe system stands out for its extremely simple handling and minimal number of components. Almost all operations can be carried out without tools, and the working steps are very straightforward. Even inexperienced users can work quickly and efficiently with DUO.\n\nA key innovation is the material: both the panels (including the form-facing) and accessories are made of high-performance technical polymers. Thanks to its very low weight, the system can be assembled and handled without the need for a crane.",
        list: null,
      },
      {
        heading: "Key Advantages",
        tag: "BENEFITS",
        body: null,
        list: [
          {
            label: "Universally Applicable",
            detail: "One single system for walls, columns, and slabs — reducing equipment inventory and simplifying logistics on site.",
          },
          {
            label: "Highly Flexible",
            detail: "Easy forming of various shapes and configurations, adaptable to project-specific geometric requirements.",
          },
          {
            label: "Ergonomic Design",
            detail: "Lightweight, crane-free handling and intuitive assembly reduces labour requirements and accelerates cycle times significantly.",
          },
          {
            label: "Easy Maintenance",
            detail: "Form-lining can be replaced quickly and simply, extending the system's usable life and keeping costs down.",
          },
          {
            label: "Quick Repairs",
            detail: "Minor damage can be fixed with just a few screws — no special skills required, minimising downtime on site.",
          },
        ],
      },
      {
        heading: "Technical Specifications",
        tag: "DETAILS",
        body: "Lightweight panel formwork made from innovative technical polymers — highly resistant to environmental influences and 100% recyclable.\n\nPanel heights: 600 mm and 1,350 mm. Panel widths: up to 900 mm. Maximum permissible fresh concrete pressure: 50 kN/m² (for walls) and 80 kN/m² (for columns and wall sections).\n\nSuitable for square and rectangular columns from 150 mm × 150 mm up to 550 mm × 550 mm (in 50 mm increments). Optimised for slabs up to 300 mm thick. Low-noise assembly — no hammer required.",
        list: null,
      },
    ],
  },

  /* ══════════════════════════════════════════
     3. RETAINING WALLS
  ══════════════════════════════════════════ */
  "retaining-walls": {
    title: "Retaining Walls",
    subtitle: "Lateral Earth Support · Engineered Design · Durable Construction",
    heroLabel: "EARTH RETENTION",
    accentStat: [
      { val: "360", unit: "° coverage", label: "Full Perimeter Retention" },
      { val: "6",   unit: "m+",         label: "Walls Requiring Pr Eng" },
      { val: "50",  unit: "+ years",    label: "Design Service Life" },
    ],
    images: [
      { src: ferrobank01, caption: "Retaining Wall Formation" },
      { src: ferrobank02, caption: "Reinforced Masonry Wall" },
      { src: ferrobank03, caption: "Completed Retaining Structure" },
    ],
    intro:
      "Retaining walls are engineered structures designed to hold back soil laterally, preventing erosion and slope failure. Our team designs and constructs walls suited to any terrain, loading condition, and aesthetic requirement — with certified structural design on all critical walls.",
    sections: [
      {
        heading: "What is a Retaining Wall?",
        tag: "OVERVIEW",
        body: "A retaining wall is a structure designed to resist the lateral pressure of soil when there is a desired change in ground elevation that exceeds the angle of repose of the soil. They are used extensively in landscaping, road construction, and building foundations.\n\nRetaining walls must be designed to withstand the full lateral earth pressure behind them, as well as any surcharge loads (vehicles, buildings, or stored materials) placed on the retained soil. Proper drainage is also critical — hydrostatic pressure behind an improperly drained wall can dramatically increase the load and lead to failure.",
        list: null,
      },
      {
        heading: "Types of Retaining Walls",
        tag: "SYSTEMS",
        body: null,
        list: [
          {
            label: "Gravity Walls",
            detail: "Rely on their own weight to resist lateral earth pressure. Typically constructed from concrete, stone, or masonry. Simple and effective for lower wall heights.",
          },
          {
            label: "Cantilever Walls",
            detail: "Thin, reinforced concrete stems that use the weight of the soil on the base slab to provide stability. Most economical for heights up to 6–7 m.",
          },
          {
            label: "Anchored Walls",
            detail: "Use cables or rods anchored into the soil or rock behind the wall. Suitable for tall walls where space for a large base is limited.",
          },
          {
            label: "Gabion Walls",
            detail: "Wire mesh baskets filled with stones. Flexible and permeable, ideal for erosion control and environmentally sensitive sites.",
          },
          {
            label: "Reinforced Earth / MSE Walls",
            detail: "Use layers of geosynthetic or metallic reinforcement within compacted fill to form a coherent reinforced mass. Economical for large wall areas.",
          },
        ],
      },
      {
        heading: "Design & Construction",
        tag: "METHODOLOGY",
        body: "All retaining walls above a certain height in South Africa require design by a registered Professional Engineer (Pr Eng). Our design process includes thorough geotechnical investigation, stability analysis (sliding, overturning, bearing capacity), and drainage design.\n\nDuring construction, we ensure correct foundation preparation, reinforcement placement, concrete quality, and waterproofing / drainage installation. Post-construction monitoring and backfill compaction control are also part of our service.",
        list: null,
      },
    ],
  },

  /* ══════════════════════════════════════════
     4. SUSPENDED SLAB
  ══════════════════════════════════════════ */
  "suspended-slab": {
    title: "Suspended Slab",
    subtitle: "One-Way · Two-Way · Cantilever · In-Situ & Precast",
    heroLabel: "ELEVATED STRUCTURES",
    accentStat: [
      { val: "2",   unit: "slab types", label: "One-Way & Two-Way" },
      { val: "300", unit: "mm max",     label: "DUO Optimised Depth" },
      { val: "28",  unit: "days",       label: "Full Strength Cure" },
    ],
    images: [
      { src: g4mining01, caption: "Suspended Slab Formation" },
      { src: g4mining02, caption: "Reinforcement Layout" },
      { src: g4mining03, caption: "Propping System" },
      { src: g4mining04, caption: "Completed Upper Floor Slab" },
    ],
    intro:
      "Suspended concrete slabs are elevated slabs that do not rest directly on the ground. They are used for upper floors, rooftops, cantilevered staircases, and striking architectural features — requiring careful structural design, correct formwork, and adequate propping during construction.",
    sections: [
      {
        heading: "What is a Suspended Slab?",
        tag: "OVERVIEW",
        body: "Suspended concrete slabs are elevated slabs that do not rest directly on the ground. They are commonly used for upper floors, rooftops, and other structures where the slab spans between supports such as beams, walls, or columns.\n\nIn addition to standard flooring and roofing applications, suspended slabs are also used to create striking architectural features such as cantilevered staircases and decorative elements. Suspended slabs are divided into two main types based on how they are supported: one-way slabs (supported on two opposite sides) and two-way slabs (supported on all four sides).\n\nLike all suspended structures, they require careful structural design, proper formwork, and adequate propping during construction until the concrete reaches sufficient strength.",
        list: null,
      },
      {
        heading: "One-Way vs Two-Way Slabs",
        tag: "SLAB TYPES",
        body: "The type of slab is determined by the span ratio, calculated as: Longer Span ÷ Shorter Span.\n\nIf the ratio is 2 or greater → One-way slab. If the ratio is less than 2 → Two-way slab.",
        list: [
          {
            label: "One-Way Slab",
            detail: "The main load is transferred predominantly in one direction — along the shorter span. Main reinforcement bars are placed in one direction only (parallel to the shorter span) to resist bending moments. Temperature or shrinkage reinforcement (distribution steel) is placed in the perpendicular direction to control cracking.",
          },
          {
            label: "Two-Way Slab",
            detail: "The load is distributed more evenly in both directions because the spans are relatively similar. Main reinforcement is required in both directions (short and long spans) to effectively carry and distribute the loads.",
          },
        ],
      },
      {
        heading: "Key Benefits",
        tag: "ADVANTAGES",
        body: null,
        list: [
          {
            label: "Excellent Thermal Mass",
            detail: "High thermal capacity helps regulate building temperatures, improving energy efficiency across seasons.",
          },
          {
            label: "Superior Sound Insulation",
            detail: "The mass of concrete slabs provides excellent acoustic separation between floors.",
          },
          {
            label: "High Durability & Low Maintenance",
            detail: "Correctly designed suspended slabs offer very long service lives with minimal ongoing maintenance costs.",
          },
          {
            label: "Construction Flexibility",
            detail: "Can be cast in-situ (poured on site) or pre-cast in a factory and transported to site, depending on project logistics and programme requirements.",
          },
        ],
      },
    ],
  },

  /* ══════════════════════════════════════════
     5. RING BEAM
  ══════════════════════════════════════════ */
  "ring-beam": {
    title: "Ring Beam",
    subtitle: "Masonry Ring Beam · Edge Beam · Horizontal Bracing",
    heroLabel: "STRUCTURAL INTEGRITY",
    accentStat: [
      { val: "360", unit: "° tie",  label: "Continuous Wall Connection" },
      { val: "6",   unit: "m max", label: "Horizontal Tie Centres" },
      { val: "Pr",  unit: "Eng.",  label: "Certified Design" },
    ],
    images: [
      { src: aerosud01, caption: "Ring Beam Formwork" },
      { src: aerosud02, caption: "Reinforcement Placement" },
      { src: aerosud03, caption: "Edge Beam Construction" },
      { src: aerosud04, caption: "Corner Detailing" },
      { src: aerosud05, caption: "Completed Ring Beam" },
    ],
    intro:
      "A ring beam is a continuous horizontal structural element that ties together exterior and load-bearing interior walls, creating a unified structural system. It distributes horizontal forces, resists deformation, and significantly improves the overall stability and crack resistance of the building.",
    sections: [
      {
        heading: "What is a Ring Beam?",
        tag: "OVERVIEW",
        body: "A ring beam, also known as a masonry ring beam or edge beam, is a continuous horizontal structural element usually made of reinforced concrete or reinforced masonry. It is typically located at ceiling or slab level and often runs beneath the roof structure. Its primary role is to tie together the exterior walls and load-bearing interior walls, creating a unified structural system.\n\nRing beams resist tensile and compressive forces, distribute concentrated loads, anchor floor and roof diaphragms, and significantly improve the overall stability, deformation control, and crack resistance of the building.",
        list: null,
      },
      {
        heading: "Key Functions",
        tag: "STRUCTURAL ROLES",
        body: null,
        list: [
          {
            label: "Horizontal Bracing",
            detail: "Acts as horizontal bracing for the building, distributing horizontal forces from wind, temperature changes, and earthquakes evenly around the building perimeter.",
          },
          {
            label: "Wall Stability",
            detail: "Prevents walls from buckling outward under loading or seismic activity, maintaining the structural envelope.",
          },
          {
            label: "Slab Edge Support",
            detail: "Provides proper support to slab edges, preventing differential movement between the slab and surrounding walls.",
          },
          {
            label: "Structural Integration",
            detail: "Improves overall integrity by connecting walls, columns, and slabs into a unified load-sharing system that performs as a whole.",
          },
        ],
      },
      {
        heading: "Construction Methods",
        tag: "HOW WE BUILD",
        body: "Ring beams can be constructed in several ways: cast-in-place (in-situ) reinforced concrete, precast elements with cast-in-place joints for continuity, or reinforced masonry belt (common in masonry buildings).\n\nFor maximum structural effectiveness, continuity at corners and reliable force transfer at construction joints are essential. Special attention must be given where the ring beam connects to diaphragms, parapets, or gables. In masonry buildings, ring beams are often formed using U-shaped masonry units filled with concrete and reinforcement.",
        list: null,
      },
      {
        heading: "Structural Detailing",
        tag: "ENGINEERING",
        body: "The ring beam normally contains both longitudinal and transverse reinforcement. It must be rigidly connected to columns, shear walls, and slabs using lap splices, welding, or mechanical connections.\n\nKey considerations include adequate concrete cover and corrosion protection, staggered lap splices for continuity, additional confinement reinforcement at corners and openings, proper torsional stiffness where eccentric loads occur (e.g., at balconies or slab edges), and secure anchorage to prevent twisting or local overstressing.",
        list: null,
      },
    ],
  },

  /* ══════════════════════════════════════════
     6. TRANSPORT
  ══════════════════════════════════════════ */
  "transport": {
    title: "Transport",
    subtitle: "Material Logistics · Plant Movement · Site Delivery",
    heroLabel: "CONSTRUCTION LOGISTICS",
    accentStat: [
      { val: "2",   unit: "hr max",  label: "Ready-Mix Delivery Window" },
      { val: "GPS", unit: "tracked", label: "Critical Deliveries" },
      { val: "JIT", unit: "method",  label: "Just-In-Time Logistics" },
    ],
    images: [
      { src: dannhauser02, caption: "Site Material Delivery" },
      { src: dannhauser03, caption: "Heavy Haulage Transport" },
    ],
    intro:
      "Transport in construction refers to the planning, logistics, and physical movement of materials, equipment, plant, and personnel to, from, and within a construction site. Efficient transport management directly impacts cost, schedule, safety, and environmental performance.",
    sections: [
      {
        heading: "Why Transport Matters",
        tag: "IMPORTANCE",
        body: "Construction projects are often material-intensive. Delays in delivery can halt progress and increase costs significantly. Large or heavy items — steel beams, precast concrete, formwork panels, scaffolding components — require specialised transport and lifting arrangements.\n\nPoor transport planning can lead to congestion on site, double handling of materials, damage to goods, and safety incidents. In South Africa, transport must comply with the National Road Traffic Act, overloading regulations, and abnormal load permits where applicable.",
        list: null,
      },
      {
        heading: "Types of Transport Services",
        tag: "SERVICES",
        body: null,
        list: [
          {
            label: "Material Transport",
            detail: "Bulk materials (sand, stone, aggregates, cement, ready-mix concrete), packaged materials (bricks, blocks, roof tiles, reinforcing steel), and prefabricated elements such as precast slabs, beams, and formwork systems.",
          },
          {
            label: "Plant & Equipment Transport",
            detail: "Movement of cranes, excavators, TLBs, rollers, generators, and concrete pumps. Low-bed trailers and abnormal load transport for oversized machinery.",
          },
          {
            label: "Personnel Transport",
            detail: "Daily transport of workers to and from site — especially in remote or urban projects. Shuttle services and bus hire for large labour forces.",
          },
          {
            label: "Specialised Heavy Haulage",
            detail: "Low-bed, extendable, and multi-axle trailers for heavy or oversized loads. Requires permits, route surveys, and escort vehicles in compliance with South African legislation.",
          },
          {
            label: "Ready-Mix Concrete Delivery",
            detail: "Agitator trucks with strict time management — usually within 2 hours of batching to maintain concrete workability and comply with SANS standards.",
          },
          {
            label: "On-Site Logistics",
            detail: "Skips, forklifts, telehandlers, and conveyor systems for internal site movement, ensuring materials reach the right location efficiently.",
          },
        ],
      },
      {
        heading: "Best Practices",
        tag: "METHODOLOGY",
        body: "Early transport planning during the tender and pre-construction phase ensures smooth project execution. We coordinate with suppliers and transporters to ensure Just-In-Time (JIT) deliveries, implement proper loading, securing, and offloading procedures, and use GPS tracking technology for critical deliveries.\n\nWell-managed transport services reduce project costs, minimise delays, improve site safety, and contribute to smoother coordination between temporary works (formwork, scaffolding) and permanent works (suspended slabs, ring beams, retaining walls).",
        list: null,
      },
    ],
  },

  /* ══════════════════════════════════════════
     7. TEMPORARY WORK DESIGN
  ══════════════════════════════════════════ */
  "temporary-work-design": {
    title: "Temporary Work Design",
    subtitle: "Falsework · Shoring · Propping · Temporary Structures",
    heroLabel: "TEMPORARY WORKS",
    accentStat: [
      { val: "40",  unit: "% of cost", label: "Typical Project Share" },
      { val: "TWC", unit: "coord.",    label: "Dedicated Coordinator" },
      { val: "0",   unit: "defects",   label: "Zero-Tolerance Safety" },
    ],
    images: [
      { src: kuruman01,  caption: "Temporary Propping System" },
      { src: aerosud04,  caption: "Falsework Design" },
      { src: kuruman05,  caption: "Temporary Works on Site" },
    ],
    intro:
      "Temporary works refer to all structures, supports, and installations built to enable the construction of permanent works but not intended to remain in the finished structure. They are essential for safety, access, and structural support — and can account for 20–40% of total project cost.",
    sections: [
      {
        heading: "What Are Temporary Works?",
        tag: "OVERVIEW",
        body: "Temporary works are often one of the largest cost components in a construction project — sometimes accounting for 20% to 40% of the total project cost, depending on the complexity of the build. Because they are removed after use, their value is not reflected in the final asset, making cost control and efficiency critical.\n\nA significant failure in temporary works can have catastrophic consequences, including serious injury or loss of life, major delays to the project programme, substantial cost overruns, damage to the permanent structure, and reputational harm to all parties involved.\n\nFor this reason, temporary works must be carefully planned, designed, installed, inspected, and dismantled by competent personnel.",
        list: null,
      },
      {
        heading: "Common Types",
        tag: "SYSTEMS",
        body: null,
        list: [
          {
            label: "Formwork",
            detail: "Moulds and supports for fresh concrete — traditional timber, engineered systems such as DUO, and stay-in-place systems.",
          },
          {
            label: "Scaffolding",
            detail: "Access platforms for workers and materials — trestles, independent scaffolds, mobile towers, and more.",
          },
          {
            label: "Falsework & Propping",
            detail: "Temporary supports for slabs, beams, and arches during curing and until the permanent structure gains full strength.",
          },
          {
            label: "Shoring & Excavation Support",
            detail: "Trench supports, sheet piling, and retaining systems to protect workers and adjacent structures during excavation.",
          },
          {
            label: "Temporary Bracing",
            detail: "For walls, columns, and unstable structures — critical during erection sequences to prevent premature collapse.",
          },
          {
            label: "Access & Site Infrastructure",
            detail: "Ramps, hoarding, site fencing, temporary bridges, cranage bases, piling platforms, and temporary roads.",
          },
        ],
      },
      {
        heading: "Management Process",
        tag: "KEY STAGES",
        body: null,
        list: [
          {
            label: "Design & Planning",
            detail: "Temporary works should be considered from the concept and tender stage. Proper engineering design, load calculations, and risk assessments are essential before any work begins.",
          },
          {
            label: "Competent Design",
            detail: "All temporary works require design by a qualified Temporary Works Coordinator (TWC) or designer, accounting for full construction loads, wind, vibration, and dynamic forces.",
          },
          {
            label: "Installation & Inspection",
            detail: "Only trained personnel should erect temporary works. Regular inspections (before, during, and after loading) are mandatory to ensure ongoing compliance.",
          },
          {
            label: "Loading & Monitoring",
            detail: "Concrete pouring sequences, back-propping, and load restrictions must be strictly followed. Any deviation requires re-assessment by the TWC.",
          },
          {
            label: "Dismantling",
            detail: "Safe removal procedures are as important as erection — to avoid damage to the permanent structure or injury to workers. A formal stripping procedure must be followed.",
          },
        ],
      },
      {
        heading: "Best Practices",
        tag: "STANDARDS",
        body: "Appoint a dedicated Temporary Works Coordinator (TWC) for medium to large projects. Use Temporary Works Registers to track all items from design to dismantling.\n\nEnsure clear communication between the permanent works designer and temporary works designer. Prioritise innovation (e.g., lightweight polymer systems like DUO) to reduce labour, crane dependency, and cycle times. Always factor in safety, sustainability, and reusability when selecting temporary works solutions.",
        list: null,
      },
    ],
  },

  /* ══════════════════════════════════════════
     8. PR ENGINEER SERVICES
  ══════════════════════════════════════════ */
  "pr-engineer-services": {
    title: "PR Engineer Services",
    subtitle: "ECSA Registered · Structural Design · Legal Certification",
    heroLabel: "PROFESSIONAL ENGINEERING",
    accentStat: [
      { val: "ECSA", unit: "reg.",    label: "Registered Professional Body" },
      { val: "3",    unit: "+ years", label: "Min. Supervised Training" },
      { val: "100",  unit: "% legal", label: "Compliant Sign-Off" },
    ],
    images: [
      { src: steelpoort06, caption: "Structural Engineering Design" },
      { src: steelpoort05, caption: "Professional Engineer on Site" },
    ],
    intro:
      "A Professional Engineer (Pr Eng) is a highly qualified engineer registered with the Engineering Council of South Africa (ECSA). This registration legally authorises independent professional-level engineering practice — bringing credibility, safety, and legal protection to your project from concept through to completion.",
    sections: [
      {
        heading: "What is a Pr Eng?",
        tag: "OVERVIEW",
        body: "A Professional Engineer (Pr Eng) is a highly qualified engineer registered with the Engineering Council of South Africa (ECSA). This registration legally authorises the individual to independently practise engineering at a professional level and to take full responsibility for engineering work.\n\nPr Engs are entrusted with complex and high-risk engineering tasks that demand advanced technical analysis, innovative problem-solving, sound engineering judgement, and the application of scientific and mathematical principles to real-world challenges. They frequently lead design teams, manage multidisciplinary projects, and carry significant legal, financial, and ethical accountability for the safety, functionality, and performance of the structures or systems they design.",
        list: null,
      },
      {
        heading: "Key Roles & Responsibilities",
        tag: "SERVICES",
        body: null,
        list: [
          {
            label: "Design Sign-Off",
            detail: "Undertaking and signing off on the design of permanent and temporary works — a legal requirement for many structure types in South Africa.",
          },
          {
            label: "Structural Analysis",
            detail: "Structural, civil, and geotechnical engineering design and analysis using advanced computational methods and industry-standard software.",
          },
          {
            label: "Code Compliance",
            detail: "Ensuring compliance with South African National Standards (SANS), building regulations, and industry best practices throughout the design and construction process.",
          },
          {
            label: "Risk Assessment",
            detail: "Risk assessment and mitigation for both permanent and temporary structures throughout the construction lifecycle.",
          },
          {
            label: "Temporary Works Review",
            detail: "Reviewing and approving temporary works designs — formwork, scaffolding, falsework, shoring — as the competent person or designer under the Construction Regulations.",
          },
          {
            label: "Certification",
            detail: "Certifying construction drawings and reports, and acting as a competent person or designer under the Construction Regulations to provide legal sign-off.",
          },
        ],
      },
      {
        heading: "Path to Pr Eng Status",
        tag: "QUALIFICATIONS",
        body: "To achieve Pr Eng status, an engineer must complete a rigorous ECSA assessment process including: completion of an accredited BEng or BSc(Eng) degree, a minimum period of supervised professional training (usually 3 years or more), submission of a comprehensive Engineering Report demonstrating competence, a professional review interview by ECSA assessors, and ongoing commitment to the ECSA Code of Conduct and Continuous Professional Development (CPD).",
        list: null,
      },
      {
        heading: "Why It Matters in Construction",
        tag: "LEGAL REQUIREMENT",
        body: "In South Africa, many critical construction activities legally require the involvement or sign-off of a Pr Eng, particularly for structural designs, retaining walls above certain heights, suspended slabs and complex formwork, high scaffolding or temporary works, and any work where public safety is involved.\n\nEngaging a registered Professional Engineer brings credibility, safety, and legal protection to your project from concept through to completion.",
        list: null,
      },
    ],
  },
};

/* ─────────────────────────────────────────────
   SHARED IMAGE PROPS (no drag, no context menu)
───────────────────────────────────────────── */
const noSaveProps = {
  draggable: false,
  onDragStart: (e) => e.preventDefault(),
  onContextMenu: (e) => e.preventDefault(),
};

/* ─────────────────────────────────────────────
   LIGHTBOX COMPONENT
───────────────────────────────────────────── */
function Lightbox({ images, activeIdx, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape")      onClose();
      if (e.key === "ArrowLeft")   onPrev();
      if (e.key === "ArrowRight")  onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (activeIdx === null) return null;

  return (
    <div className="sd-lightbox" onClick={onClose}>
      <button className="sd-lightbox__close" onClick={onClose}>
        <FiX size={22} />
      </button>
      <button
        className="sd-lightbox__nav sd-lightbox__nav--prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
      >
        <FiChevronLeft size={28} />
      </button>
      <div className="sd-lightbox__stage" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[activeIdx].src}
          alt={images[activeIdx].caption}
          {...noSaveProps}
        />
        <div className="sd-lightbox__caption">
          <span className="sd-lightbox__count">
            {activeIdx + 1} / {images.length}
          </span>
          {images[activeIdx].caption}
        </div>
      </div>
      <button
        className="sd-lightbox__nav sd-lightbox__nav--next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
      >
        <FiChevronRight size={28} />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE COMPONENT
───────────────────────────────────────────── */
export default function ServiceDetail() {
  useNoContextMenu();

  const { slug } = useParams();
  const data = serviceData[slug];

  const [lightboxIdx,  setLightboxIdx]  = useState(null);
  const [activeThumb,  setActiveThumb]  = useState(0);

  const [heroRef,  heroInView]  = useInView();
  const [introRef, introInView] = useInView();
  const [statsRef, statsInView] = useInView();
  const [sectRef,  sectInView]  = useInView();
  const [gallRef,  gallInView]  = useInView();
  const [navRef,   navInView]   = useInView();

  /* scroll to top on slug change & reset thumb */
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveThumb(0);
  }, [slug]);

  /* 404 guard */
  if (!data) {
    return (
      <div className="sd-not-found">
        <h2>Service not found</h2>
        <Link to="/services" className="sd-back-btn">
          <FiArrowLeft size={16} /> Back to Services
        </Link>
      </div>
    );
  }

  /* prev / next navigation */
  const currentIdx = ALL_SERVICES.findIndex((s) => s.slug === slug);
  const prevSvc    = currentIdx > 0                        ? ALL_SERVICES[currentIdx - 1] : null;
  const nextSvc    = currentIdx < ALL_SERVICES.length - 1 ? ALL_SERVICES[currentIdx + 1] : null;

  /* lightbox helpers */
  const openLightbox  = (i) => { setLightboxIdx(i); document.body.style.overflow = "hidden"; };
  const closeLightbox = ()  => { setLightboxIdx(null); document.body.style.overflow = ""; };
  const prevLight     = ()  => setLightboxIdx((i) => (i === 0 ? data.images.length - 1 : i - 1));
  const nextLight     = ()  => setLightboxIdx((i) => (i === data.images.length - 1 ? 0 : i + 1));

  return (
    <main className="sd">

      {/* ══════════════════════════════════════
          1. HERO
      ══════════════════════════════════════ */}
      <section className="sd-hero">
        <div
          className="sd-hero__bg"
          style={{ backgroundImage: `url(${data.images[0].src})` }}
        />
        <div className="sd-hero__overlay" />
        <div className="sd-hero__stripe" aria-hidden="true" />

        <div className="container sd-hero__body" ref={heroRef}>
          <Link
            to="/services"
            className={`sd-hero__back fade-in ${heroInView ? "visible" : ""}`}
          >
            <FiArrowLeft size={14} /> All Services
          </Link>
          <div
            className={`sd-hero__label fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.06s" }}
          >
            {data.heroLabel}
          </div>
          <h1
            className={`sd-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.14s" }}
          >
            {data.title}
          </h1>
          <p
            className={`sd-hero__subtitle fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.22s" }}
          >
            {data.subtitle}
          </p>
        </div>

        <div className="sd-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={11} />
          <Link to="/services">Services</Link>
          <FiArrowRight size={11} />
          <span>{data.title}</span>
        </div>
      </section>

      {/* ══════════════════════════════════════
          2. STATS BAR
      ══════════════════════════════════════ */}
      <div className="sd-stats" ref={statsRef}>
        {data.accentStat.map((s, i) => (
          <div
            key={i}
            className={`sd-stat fade-in ${statsInView ? "visible" : ""}`}
            style={{ transitionDelay: `${i * 0.1}s` }}
          >
            <div className="sd-stat__val">
              <strong>{s.val}</strong>
              <span className="sd-stat__unit">{s.unit}</span>
            </div>
            <div className="sd-stat__label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════
          3. INTRO: GALLERY + TEXT
      ══════════════════════════════════════ */}
      <section className="sd-intro" ref={introRef}>
        <div className="container sd-intro__inner">

          {/* Gallery column */}
          <div className="sd-intro__gallery-col">
            <div
              className="sd-intro__main-photo"
              onClick={() => openLightbox(activeThumb)}
            >
              <img
                src={data.images[activeThumb].src}
                alt={data.images[activeThumb].caption}
                {...noSaveProps}
              />
              <div className="sd-intro__photo-overlay">
                <span className="sd-intro__photo-caption">
                  {data.images[activeThumb].caption}
                </span>
                <span className="sd-intro__photo-zoom">⤡ Click to enlarge</span>
              </div>
              <div className="sd-intro__photo-badge">
                {activeThumb + 1}/{data.images.length}
              </div>
            </div>

            {data.images.length > 1 && (
              <div className="sd-intro__thumbs">
                {data.images.map((img, i) => (
                  <button
                    key={i}
                    className={`sd-intro__thumb ${i === activeThumb ? "sd-intro__thumb--active" : ""}`}
                    onClick={() => setActiveThumb(i)}
                  >
                    <img src={img.src} alt={img.caption} {...noSaveProps} />
                    {i === activeThumb && <div className="sd-intro__thumb-bar" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Text column */}
          <div
            className={`sd-intro__text-col fade-in ${introInView ? "visible" : ""}`}
          >
            <div className="sd-intro__tag">SERVICE OVERVIEW</div>
            <div className="sd-intro__bar" />
            <p className="sd-intro__lead">{data.intro}</p>
            <Link to="/contact" className="sd-intro__cta">
              Request a Quote
              <span className="sd-intro__cta-arrow">
                <FiArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          4. CONTENT SECTIONS
      ══════════════════════════════════════ */}
      <section className="sd-sections" ref={sectRef}>
        <div className="container sd-sections__inner">
          {data.sections.map((sec, i) => (
            <div
              key={i}
              className={`sd-sec fade-in ${sectInView ? "visible" : ""}`}
              style={{ transitionDelay: `${i * 0.09}s` }}
            >
              <div className="sd-sec__head">
                <span className="sd-sec__tag">{sec.tag}</span>
                <h2 className="sd-sec__heading">{sec.heading}</h2>
                <div className="sd-sec__rule" />
              </div>

              {sec.body && (
                <div className="sd-sec__body">
                  {sec.body.split("\n\n").map((para, j) => (
                    <p key={j}>{para}</p>
                  ))}
                </div>
              )}

              {sec.list && (
                <div className="sd-sec__list">
                  {sec.list.map((item, j) => (
                    <div key={j} className="sd-sec__item">
                      <div className="sd-sec__item-num">0{j + 1}</div>
                      <div className="sd-sec__item-content">
                        <h4 className="sd-sec__item-label">{item.label}</h4>
                        <p className="sd-sec__item-detail">{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════
          5. FULL GALLERY STRIP
      ══════════════════════════════════════ */}
      {data.images.length > 1 && (
        <section className="sd-gallery" ref={gallRef}>
          <div className="container">
            <div
              className={`sd-gallery__head fade-in ${gallInView ? "visible" : ""}`}
            >
              <span className="sd-gallery__tag">PROJECT GALLERY</span>
              <h3 className="sd-gallery__h3">View Our Work</h3>
            </div>
            <div className="sd-gallery__grid">
              {data.images.map((img, i) => (
                <button
                  key={i}
                  className={`sd-gallery__item fade-in ${gallInView ? "visible" : ""}`}
                  style={{ transitionDelay: `${i * 0.07}s` }}
                  onClick={() => openLightbox(i)}
                >
                  <img src={img.src} alt={img.caption} {...noSaveProps} />
                  <div className="sd-gallery__item-overlay">
                    <span className="sd-gallery__item-caption">{img.caption}</span>
                    <span className="sd-gallery__item-icon">⤡</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          6. PREV / NEXT NAVIGATION
      ══════════════════════════════════════ */}
      <nav className="sd-service-nav" ref={navRef}>
        <div className="container sd-service-nav__inner">
          {prevSvc ? (
            <Link
              to={`/services/${prevSvc.slug}`}
              className={`sd-service-nav__btn sd-service-nav__btn--prev fade-in ${navInView ? "visible" : ""}`}
            >
              <FiChevronLeft size={20} />
              <span>
                <small>Previous Service</small>
                {prevSvc.short}
              </span>
            </Link>
          ) : (
            <div />
          )}

          <Link
            to="/services"
            className={`sd-service-nav__all fade-in ${navInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            All Services
          </Link>

          {nextSvc ? (
            <Link
              to={`/services/${nextSvc.slug}`}
              className={`sd-service-nav__btn sd-service-nav__btn--next fade-in ${navInView ? "visible" : ""}`}
              style={{ transitionDelay: "0.2s" }}
            >
              <span>
                <small>Next Service</small>
                {nextSvc.short}
              </span>
              <FiChevronRight size={20} />
            </Link>
          ) : (
            <div />
          )}
        </div>
      </nav>

      {/* ══════════════════════════════════════
          7. CTA BAND
      ══════════════════════════════════════ */}
      <section className="sd-cta">
        <div className="container sd-cta__inner">
          <div className="sd-cta__text">
            <h3>Ready to discuss your project?</h3>
            <p>Our team of experts is on hand to provide tailored solutions.</p>
          </div>
          <Link to="/contact" className="sd-cta__btn">
            Get a Quote <FiArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ══════════════════════════════════════
          LIGHTBOX
      ══════════════════════════════════════ */}
      <Lightbox
        images={data.images}
        activeIdx={lightboxIdx}
        onClose={closeLightbox}
        onPrev={prevLight}
        onNext={nextLight}
      />
    </main>
  );
}